import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle, Users, BookOpen, DollarSign, ShieldAlert,
  RefreshCw, ChevronDown, Filter, Loader2, CheckCircle2, Info,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';

const RISK_META = {
  attendance: { icon: Users, color: 'orange', label: 'Attendance', bg: 'bg-orange-50', border: 'border-orange-200', badge: 'bg-orange-100 text-orange-700', dot: 'bg-orange-400' },
  academic:   { icon: BookOpen, color: 'red', label: 'Academic', bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-700', dot: 'bg-red-400' },
  fee:        { icon: DollarSign, color: 'yellow', label: 'Fee', bg: 'bg-yellow-50', border: 'border-yellow-200', badge: 'bg-yellow-100 text-yellow-700', dot: 'bg-yellow-400' },
  behavior:   { icon: ShieldAlert, color: 'purple', label: 'Behavior', bg: 'bg-purple-50', border: 'border-purple-200', badge: 'bg-purple-100 text-purple-700', dot: 'bg-purple-400' },
};

const LEVEL_BADGE = {
  high:   'bg-red-100 text-red-700 border border-red-200',
  medium: 'bg-orange-100 text-orange-700 border border-orange-200',
};

function SummaryCard({ icon: Icon, label, count, color, active, onClick }) {
  return (
    <button
      data-testid={`risk-filter-${label.toLowerCase()}`}
      onClick={onClick}
      className={`flex items-center gap-3 p-4 rounded-xl border transition-all text-left ${
        active ? `border-${color}-400 bg-${color}-50 shadow-sm` : 'border-slate-200 bg-white hover:border-slate-300'
      }`}
    >
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${active ? `bg-${color}-100` : 'bg-slate-100'}`}>
        <Icon className={`w-4 h-4 ${active ? `text-${color}-600` : 'text-slate-500'}`} />
      </div>
      <div>
        <p className={`text-xl font-bold ${active ? `text-${color}-700` : 'text-slate-800'}`}>{count}</p>
        <p className="text-xs text-slate-500">{label}</p>
      </div>
    </button>
  );
}

export default function EarlyWarningsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [classes, setClasses] = useState([]);
  const [filterType, setFilterType] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api.get('/academics/classes').then(r => setClasses(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType) params.set('risk_type', filterType);
      if (filterClass) params.set('class_id', filterClass);
      const res = await api.get(`/analytics/early-warnings?${params.toString()}`);
      if (res.success) setData(res.data);
    } catch {
      toast.error('Failed to load early warnings');
    } finally {
      setLoading(false);
    }
  }, [filterType, filterClass]);

  useEffect(() => { load(); }, [load]);

  const students = (data?.students || []).filter(s => !filterLevel || s.risk_level === filterLevel);
  const summary = data?.summary || {};

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="early-warnings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Early Warning System
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Proactive detection of students at risk — attendance, academic, fee, and behavior
          </p>
        </div>
        <button
          data-testid="refresh-warnings-btn"
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-[#2563eb] text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition disabled:opacity-60"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <SummaryCard
          icon={AlertTriangle} label="All Risks" count={summary.total || 0}
          color="slate" active={!filterType} onClick={() => setFilterType('')}
        />
        {Object.entries(RISK_META).map(([type, meta]) => (
          <SummaryCard
            key={type}
            icon={meta.icon}
            label={meta.label}
            count={summary.by_type?.[type] || 0}
            color={meta.color}
            active={filterType === type}
            onClick={() => setFilterType(filterType === type ? '' : type)}
          />
        ))}
      </div>

      {/* Filters Row */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-5 flex flex-wrap gap-3 items-center">
        <Filter className="w-4 h-4 text-slate-400" />
        <div className="relative">
          <select
            value={filterClass}
            onChange={e => setFilterClass(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white outline-none appearance-none pr-7 min-w-[140px]"
            data-testid="filter-class-select"
          >
            <option value="">All Classes</option>
            {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
        </div>
        <div className="relative">
          <select
            value={filterLevel}
            onChange={e => setFilterLevel(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white outline-none appearance-none pr-7 min-w-[140px]"
            data-testid="filter-level-select"
          >
            <option value="">All Risk Levels</option>
            <option value="high">High Risk</option>
            <option value="medium">Medium Risk</option>
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
        </div>
        <div className="ml-auto flex items-center gap-2 text-sm text-slate-500">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          <span>{students.length} student{students.length !== 1 ? 's' : ''} flagged</span>
        </div>
      </div>

      {/* High risk alert banner */}
      {summary.high > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-5 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-800">
              {summary.high} student{summary.high !== 1 ? 's' : ''} at HIGH risk — immediate action recommended
            </p>
            <p className="text-xs text-red-600 mt-0.5">
              High-risk students are flagged in red below. Review and escalate interventions promptly.
            </p>
          </div>
        </div>
      )}

      {/* Student Risk List */}
      {loading && !data && (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-20 bg-slate-100 rounded-xl animate-pulse" />
          ))}
        </div>
      )}

      {!loading && students.length === 0 && (
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-16 text-center">
          <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
          <p className="font-semibold text-slate-700">No students flagged</p>
          <p className="text-sm text-slate-500 mt-1">
            {filterType || filterClass || filterLevel
              ? 'No results for the selected filters.'
              : 'All students appear to be on track based on available data.'}
          </p>
        </div>
      )}

      <div className="space-y-3">
        {students.map(st => {
          const meta = RISK_META[st.risk_type] || RISK_META.attendance;
          const Icon = meta.icon;
          const isOpen = expanded === `${st.student_id}-${st.risk_type}`;

          return (
            <div
              key={`${st.student_id}-${st.risk_type}`}
              data-testid={`risk-card-${st.risk_type}`}
              className={`rounded-xl border ${meta.border} ${meta.bg} overflow-hidden`}
            >
              <button
                className="w-full flex items-center gap-4 p-4 text-left"
                onClick={() => setExpanded(isOpen ? null : `${st.student_id}-${st.risk_type}`)}
              >
                {/* Avatar */}
                <div className={`w-9 h-9 rounded-full bg-white flex items-center justify-center text-sm font-bold text-slate-600 border border-slate-200 flex-shrink-0`}>
                  {(st.student_name || '?')[0].toUpperCase()}
                </div>

                {/* Student info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-800 text-sm">{st.student_name}</span>
                    <span className="text-xs text-slate-400">{st.admission_number}</span>
                    {st.class_name && (
                      <span className="text-xs bg-white border border-slate-200 rounded px-1.5 py-0.5 text-slate-600">
                        {st.class_name}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-600 mt-0.5">{st.detail}</p>
                </div>

                {/* Badges */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${meta.badge}`}>
                    {meta.label}
                  </span>
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${LEVEL_BADGE[st.risk_level]}`}>
                    {st.risk_level?.toUpperCase()}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </div>
              </button>

              {/* Intervention panel */}
              {isOpen && (
                <div className="border-t border-white/60 px-4 py-3 bg-white/40">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-slate-700 mb-0.5">Recommended Intervention</p>
                      <p className="text-sm text-slate-600">{st.intervention}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
